import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rMultiple, targetKey } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { dirClass, dirLabel, fmtCountdown, fmtR, fmtTime, formatMoney, signClass, titleCase, useNow } from '../lib/format';
import type { TradeView } from '../lib/types';
import type { HandoverView } from '../lib/types-setups';
import { HANDOVER_RIGHTS_LABEL, handoverSummaryText } from '@vtf/shared';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Pill, Segmented, Textarea } from '../components/ui';
import { DiscussLive } from './Rooms';
import { CommentThread, DiscussToggle, FollowButton, ReactionBar, useSocialSummary } from '../components/social';
import { periodText } from './Handovers';
import { OpenPnl, PriceLabel } from '../components/PriceMark';

interface TradeDetail extends TradeView {
  instrument: { symbol: string; quoteCurrency: string; tickSize: string };
  initialStop: string | null;
  plan: { id: string; riskPct: string | null; rationale: string | null; setupId?: string | null } | null;
  nextDue: { id: string; type: string; dueAt: string; graceUntil: string; missAt: string; status: string; sequence: number | null; assignee?: { id: string; name: string } } | null;
  events: { id: string; type: string; price: string | null; size: string | null; stopLoss: string | null; pnl: string | null; note: string | null; actor: { name: string }; onBehalfOf: { name: string } | null; occurredAt: string }[];
  progressReports: { id: string; sequence: number | null; author: { name: string }; onBehalfOf?: { name: string } | null; currentPrice: string; unrealisedR: string | null; unrealisedPnl: string | null; thesis: string; action: string; notes: string | null; isAdHoc: boolean; isLate: boolean; createdAt: string }[];
  closingReport: { followedPlan: boolean; followedPlanNote: string | null; wentWell: string | null; improve: string | null; isLate: boolean } | null;
  handovers: HandoverView[];
  access: { report: boolean; manage: boolean; covering: boolean; coverRights: 'REPORT_ONLY' | 'MANAGE' | null };
}

/** INT-PRG-05: request, answer, each covering window and the return, as timeline entries. */
function handoverEntries(h: HandoverView) {
  const out: { at: string; key: string; title: string; detail: string; tone: string }[] = [];
  const rights = HANDOVER_RIGHTS_LABEL[h.rights].toLowerCase();
  out.push({ at: h.requestedAt, key: `${h.id}-req`, title: `Handover to ${h.cover.name} requested`, detail: `${rights} · by ${h.owner.name}`, tone: 'text-live' });
  if (h.respondedAt && h.status !== 'DECLINED') out.push({ at: h.respondedAt, key: `${h.id}-acc`, title: `${h.cover.name} accepted the handover`, detail: rights, tone: 'text-live' });
  if (h.status === 'DECLINED' && h.respondedAt) out.push({ at: h.respondedAt, key: `${h.id}-dec`, title: `${h.cover.name} declined the handover`, detail: `${h.owner.name} keeps the trade`, tone: 'text-down' });
  if (h.status === 'NOT_ACCEPTED' && h.endedAt) out.push({ at: h.endedAt, key: `${h.id}-na`, title: 'Handover not accepted in time', detail: `${h.owner.name} keeps the trade`, tone: 'text-down' });
  if (h.status === 'CANCELLED' && h.endedAt) out.push({ at: h.endedAt, key: `${h.id}-can`, title: 'Handover withdrawn', detail: `by ${h.owner.name}`, tone: 'text-sub' });
  h.summary.windows.forEach((w, i) => {
    out.push({ at: w.from, key: `${h.id}-w${i}`, title: `${h.cover.name} took over`, detail: h.recurringQuietHours ? `${h.owner.name}'s quiet hours` : rights, tone: 'text-live' });
    const last = i === h.summary.windows.length - 1 && h.status === 'ENDED';
    if (!last) out.push({ at: w.to, key: `${h.id}-r${i}`, title: `Back with ${h.owner.name}`, detail: handoverSummaryText(h.cover.name, w), tone: 'text-live' });
  });
  if (h.coveringSince) out.push({ at: h.coveringSince, key: `${h.id}-now`, title: `${h.cover.name} took over`, detail: `covering now · ${rights}`, tone: 'text-live' });
  if (h.status === 'ENDED' && h.endedAt) out.push({ at: h.endedAt, key: `${h.id}-end`, title: `Handover ended · back with ${h.owner.name}`, detail: h.summary.text ?? '', tone: 'text-live' });
  return out;
}

const THESIS = [
  { value: 'VALID', label: 'Valid' },
  { value: 'WEAKENING', label: 'Weakening' },
  { value: 'INVALID', label: 'Invalid' },
] as const;
const ACTIONS = [
  { value: 'HOLD', label: 'Hold' },
  { value: 'MOVE_SL', label: 'Move stop' },
  { value: 'PARTIAL', label: 'Take partial' },
  { value: 'SCALE', label: 'Scale in' },
  { value: 'CLOSE', label: 'Close' },
] as const;

export function TradeDetailPage() {
  const { id } = useParams();
  const me = useMe();
  const qc = useQueryClient();
  const now = useNow(1000);
  const tz = me.data?.timezone ?? 'UTC';
  const { data: t, error } = useQuery({ queryKey: ['trade', id], queryFn: () => api<TradeDetail>(`/trades/${id}`) });
  const [price, setPrice] = useState('');
  // INT-SYN-04: the latest delayed price pre-fills the report once; whatever the trader types is what gets reported
  const prefilled = useRef<string | null>(null);
  useEffect(() => {
    if (t?.mark?.price && prefilled.current === null && price === '') {
      prefilled.current = t.mark.price;
      setPrice(t.mark.price);
    }
  }, [t?.mark?.price]); // eslint-disable-line react-hooks/exhaustive-deps
  const [thesis, setThesis] = useState<string>('VALID');
  const [action, setAction] = useState<string>('HOLD');
  const [notes, setNotes] = useState('');
  const [newStop, setNewStop] = useState('');
  const [partPrice, setPartPrice] = useState('');
  const [partSize, setPartSize] = useState('');
  const [closePrice, setClosePrice] = useState('');
  const [followed, setFollowed] = useState<string>('YES');
  const [wentWell, setWentWell] = useState('');
  const [improve, setImprove] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  const done = (text: string) => () => {
    setMsg(text);
    void qc.invalidateQueries({ queryKey: ['trade', id] });
    void qc.invalidateQueries({ queryKey: ['today'] });
  };
  const report = useMutation({
    mutationFn: () => api<{ isLate: boolean; sequence: number | null; isAdHoc: boolean }>(`/trades/${id}/progress-reports`, { body: { currentPrice: price, thesis, action, notes: notes || undefined } }),
    onSuccess: (r) => {
      done(r.isAdHoc ? 'Ad-hoc report posted to the floor.' : `Report #${r.sequence} posted${r.isLate ? ' (late)' : ''}.`)();
      setNotes('');
      prefilled.current = null;
      setPrice('');
    },
  });
  const moveStop = useMutation({ mutationFn: () => api(`/trades/${id}/events`, { body: { type: 'SL_MOVED', stopLoss: newStop } }), onSuccess: done('Stop updated.') });
  const partial = useMutation({ mutationFn: () => api(`/trades/${id}/events`, { body: { type: 'PARTIAL_CLOSE', price: partPrice, size: partSize } }), onSuccess: done('Partial close recorded.') });
  const close = useMutation({ mutationFn: () => api(`/trades/${id}/close`, { body: { price: closePrice } }), onSuccess: done('Trade closed. Please file the closing report.') });
  const closing = useMutation({ mutationFn: () => api(`/trades/${id}/closing-report`, { body: { followedPlan: followed === 'YES', wentWell: wentWell || undefined, improve: improve || undefined } }), onSuccess: done('Closing report filed.') });
  // discussion: the trade's own thread plus one per progress report (INT-ANL-03 applied to trades, INT-FLR-03 visibility)
  const [params] = useSearchParams();
  const focusReport = params.get('report');
  const focusComment = params.get('comment');
  const [openReports, setOpenReports] = useState<Set<string>>(() => new Set(focusReport ? [focusReport] : []));
  const social = useSocialSummary(t ? [targetKey('TRADE', t.id), ...t.progressReports.map((r) => targetKey('PROGRESS_REPORT', r.id))] : [], !!t);
  const toggleReport = (rid: string) => setOpenReports((o) => (o.has(rid) ? new Set([...o].filter((x) => x !== rid)) : new Set([...o, rid])));

  if (error) return <ErrorText error={errorText(error)} />;
  if (!t) return null;
  const canAct = t.access.report;
  const canManage = t.access.manage;
  const isOwner = t.owner.id === me.data?.id;
  const liveHandover = t.handovers.find((h) => h.status === 'REQUESTED' || h.status === 'ACCEPTED' || h.status === 'ACTIVE');
  const covering = t.handovers.find((h) => h.coveringNow);
  const estR = price && t.initialStop ? rMultiple(t.direction, t.avgEntry, price, t.initialStop) : null;
  const due = t.nextDue;
  const overdue = due && (due.status === 'OVERDUE' || due.status === 'MISSED' || new Date(due.dueAt).getTime() < now);
  const firstErr = [report, moveStop, partial, close, closing].map((m) => m.error).find(Boolean);

  const what = `${isOwner ? 'your' : `${t.owner.name}'s`} ${t.symbol} ${t.direction === 'LONG' ? 'long' : 'short'}`;
  const timeline = [
    ...t.events.map((e) => ({ at: e.occurredAt, key: e.id, reportId: null as string | null, title: `${titleCase(e.type)}${e.price ? ` @ ${e.price}` : ''}${e.stopLoss && e.type !== 'ENTRY' ? ` → stop ${e.stopLoss}` : ''}`, detail: [e.size && `size ${e.size}`, e.pnl && t.currency && formatMoney(e.pnl, t.currency, { signed: true }), e.note, `by ${e.actor.name}${e.onBehalfOf ? ` for ${e.onBehalfOf.name}` : ''}`].filter(Boolean).join(' · '), tone: e.type === 'ENTRY' ? 'text-up' : e.type === 'CLOSE' ? 'text-accent' : 'text-ink' })),
    ...t.progressReports.map((r) => ({ at: r.createdAt, key: r.id, reportId: r.id as string | null, title: `${r.isAdHoc ? 'Ad-hoc report' : `Report #${r.sequence}`}${r.isLate ? ' · late' : ''} · ${fmtR(r.unrealisedR)}`, detail: [`thesis ${r.thesis.toLowerCase()}`, titleCase(r.action), r.unrealisedPnl && t.currency && formatMoney(r.unrealisedPnl, t.currency, { signed: true }), r.notes, `by ${r.author.name}${r.onBehalfOf ? ` for ${r.onBehalfOf.name}` : ''}`].filter(Boolean).join(' · '), tone: 'text-ink' })),
    ...t.handovers.flatMap(handoverEntries).map((e) => ({ ...e, reportId: null as string | null })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <>
      <PageHeader
        title={
          <>
            {t.symbol} <span className={dirClass(t.direction)}>{dirLabel(t.direction)}</span> <span className="text-base font-normal text-muted">· {t.account.code}{t.money ? ` · ${t.status === 'OPEN' ? t.sizeOpen : t.sizeInitial}` : ''}</span>
          </>
        }
        sub={`Entry ${t.avgEntry} · stop ${t.stopLoss ?? '—'} · target ${t.takeProfits.map((x) => x.price).join(' / ') || '—'} · opened ${fmtTime(t.openedAt, tz, true)} by ${t.owner.name}${t.isUnplanned ? ' · unplanned' : ''}`}
        back={<Link to="/" className="text-[13px]">← Today</Link>}
        right={
          <>
            {!isOwner && <FollowButton targetType="USER" targetId={t.owner.id} label={t.owner.name} />}
            {t.status === 'OPEN' && (isOwner || canManage || hasRole(me.data?.roles, 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN')) && <DiscussLive type="TRADE" id={t.id} />}
            {t.status === 'CLOSED' ? (
              <Pill tone={Number(t.realisedR) >= 0 ? 'up' : 'down'}>Closed · {fmtR(t.realisedR)}{t.realisedPnl && t.currency ? ` · ${formatMoney(t.realisedPnl, t.currency, { signed: true })}` : ''}</Pill>
            ) : due ? (
              <Pill tone={overdue ? 'down' : 'accent'}>
                {due.type === 'PROGRESS_REPORT' ? `Report #${due.sequence}` : titleCase(due.type)} {due.status === 'MISSED' ? 'missed' : fmtCountdown(due.dueAt, now)}
              </Pill>
            ) : null}
          </>
        }
      />
      {msg && <p className="m-0 rounded-lg border border-[#1e5a3c] bg-up-bg px-3 py-2 text-sm text-up">{msg}</p>}
      {t.status === 'OPEN' && t.mark && (
        <Card tone={t.mark.stale ? 'warn' : undefined} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="flex flex-col gap-0.5">
            <span className="text-xs text-muted">Marked to the delayed price{t.mark.proxyNote ? ` · ${t.mark.proxyNote}` : ` · ${t.mark.sourceSymbol}`}</span>
            <span className="font-mono text-base font-semibold">{t.mark.price}</span>
          </span>
          <OpenPnl mark={t.mark} tz={tz} />
          <span className="basis-full text-xs text-muted">
            {t.mark.stale ? 'This price is older than the floor allows, so treat the figures as stale. ' : ''}
            Prices come from {t.mark.sourceName}'s free public data: delayed, for orientation only. Your broker's price is the one that counts.
            {!t.mark.money && ' Money amounts are shown to the trader and the risk desk only.'}
          </span>
        </Card>
      )}
      <ErrorText error={firstErr ? errorText(firstErr) : null} />
      {t.status === 'OPEN' && (liveHandover || isOwner) && (
        <Card tone={liveHandover ? 'live' : undefined} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="flex flex-wrap items-center gap-2 text-ink2">
            {liveHandover ? (
              <>
                <Pill tone="live">{covering ? 'Covered now' : liveHandover.status === 'REQUESTED' ? 'Handover requested' : 'Handover arranged'}</Pill>
                <span>
                  {t.access.covering ? `You're covering this trade for ${t.owner.name}` : `${liveHandover.cover.name} ${covering ? 'is covering' : liveHandover.status === 'REQUESTED' ? 'has been asked to cover' : 'covers'} this trade`} · {HANDOVER_RIGHTS_LABEL[liveHandover.rights].toLowerCase()} · {periodText(liveHandover, tz)}
                </span>
              </>
            ) : (
              <span className="text-sub">Going offline or into quiet hours? Hand this trade over so reporting continues.</span>
            )}
          </span>
          {isOwner && (
            <Link to={liveHandover ? '/handovers' : `/handovers/new?trade=${t.id}`} className="rounded-lg border border-[#2b3340] bg-[#1a1f27] px-3.5 py-2 text-sm font-semibold text-ink no-underline hover:text-ink">
              {liveHandover ? 'Manage handover' : 'Hand over'}
            </Link>
          )}
        </Card>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_460px] flex-col gap-3.5">
          {canAct && (
            <Card tone={overdue ? 'down' : undefined} className="flex flex-col gap-3.5 p-4">
              <h2 className="m-0 text-base font-bold">
                {due?.type === 'PROGRESS_REPORT' ? `Progress report #${due.sequence}` : 'Progress report'}
                {t.access.covering ? <span className="font-normal text-sub"> · for {t.owner.name}</span> : null}
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Current price"
                  hint={
                    t.mark && price === t.mark.price ? (
                      <>
                        Pre-filled · <PriceLabel mark={t.mark} tz={tz} className="text-xs" /> — check it against your broker and change it if needed.
                      </>
                    ) : t.mark ? (
                      'Your price is the one reported.'
                    ) : undefined
                  }
                >
                  <Input mono inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
                </Field>
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs text-muted">Unrealised (estimate)</span>
                  <span className={cx('py-2.5 font-mono text-base font-semibold', signClass(estR))}>{fmtR(estR)}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">Is the thesis still valid?</span>
                <Segmented label="Thesis" options={[...THESIS]} value={thesis} onChange={setThesis} colors={{ VALID: '#3dd68c', WEAKENING: '#f5b544', INVALID: '#ff7a6e' }} />
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">Action taken or planned</span>
                <Segmented label="Action" options={[...ACTIONS]} value={action} onChange={setAction} />
              </div>
              <Field label="Notes">
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              <div className="flex flex-wrap items-center justify-between gap-2">
                {overdue ? <span className="text-xs text-warn">Submitting now marks this report late.</span> : <span />}
                <Button variant="primary" onClick={() => report.mutate()} disabled={!price || report.isPending}>
                  Submit report
                </Button>
              </div>
            </Card>
          )}

          {t.status === 'CLOSED' && isOwner && !t.closingReport && (
            <Card tone="warn" className="flex flex-col gap-3 p-4">
              <h2 className="m-0 text-base font-bold">Closing report</h2>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted">Did you follow the plan?</span>
                <Segmented label="Followed plan" options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]} value={followed} onChange={setFollowed} />
              </div>
              <Field label="What went well">
                <Textarea value={wentWell} onChange={(e) => setWentWell(e.target.value)} />
              </Field>
              <Field label="What to improve">
                <Textarea value={improve} onChange={(e) => setImprove(e.target.value)} />
              </Field>
              <Button variant="primary" className="self-start" onClick={() => closing.mutate()} disabled={closing.isPending}>
                File closing report
              </Button>
            </Card>
          )}
          {t.closingReport && (
            <Card className="flex flex-col gap-1.5 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Closing report {t.closingReport.isLate && <Pill tone="warn">late</Pill>}</h2>
              <span>Followed plan: {t.closingReport.followedPlan ? 'yes' : 'no'}</span>
              {t.closingReport.wentWell && <span className="text-ink2">Went well: {t.closingReport.wentWell}</span>}
              {t.closingReport.improve && <span className="text-ink2">Improve: {t.closingReport.improve}</span>}
            </Card>
          )}

          <Card>
            <CardHeader title="Trade timeline" />
            {timeline.map((e) => {
              const sum = e.reportId ? social.data?.[targetKey('PROGRESS_REPORT', e.reportId)] : undefined;
              const isOpen = !!e.reportId && openReports.has(e.reportId);
              return (
                <div key={e.key} id={e.reportId ? `report-${e.reportId}` : undefined} className="flex flex-col gap-2 border-t border-[#1a1f27] px-4 py-2.5 first:border-t-0">
                  <div className="flex gap-3.5">
                    <span className="min-w-[96px] font-mono text-xs text-muted">{fmtTime(e.at, tz, true)}</span>
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className={cx('font-semibold', e.tone)}>{e.title}</span>
                      <span className="text-xs text-sub">{e.detail}</span>
                      {e.reportId && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <ReactionBar targetType="PROGRESS_REPORT" targetId={e.reportId} kinds={['LIKE']} summary={sum} readOnly={isOwner} what={`this report`} />
                          <DiscussToggle count={sum?.comments ?? 0} open={isOpen} onClick={() => toggleReport(e.reportId!)} controls={`thread-${e.reportId}`} />
                        </div>
                      )}
                    </div>
                  </div>
                  {isOpen && (
                    <div id={`thread-${e.reportId}`} className="sm:pl-[110px]">
                      <CommentThread targetType="PROGRESS_REPORT" targetId={e.reportId!} what={`this report on ${what}`} highlight={focusReport === e.reportId ? focusComment : null} autoFocus={focusReport !== e.reportId} />
                    </div>
                  )}
                </div>
              );
            })}
            {!timeline.length && <Empty>No events yet.</Empty>}
          </Card>

          <Card className="flex flex-col gap-3 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="m-0 text-[15px] font-semibold">Discussion</h2>
              <ReactionBar targetType="TRADE" targetId={t.id} kinds={['LIKE']} summary={social.data?.[targetKey('TRADE', t.id)]} readOnly={isOwner} what="this trade" />
            </div>
            <CommentThread targetType="TRADE" targetId={t.id} what={what} highlight={!focusReport ? focusComment : null} />
          </Card>
        </section>

        {canAct && !canManage && t.access.covering && (
          <aside aria-label="Manage trade" className="flex min-w-0 flex-[1_1_320px] flex-col gap-3.5">
            <Card className="flex flex-col gap-2 p-4">
              <div className="flex items-center justify-between gap-2">
                <h2 className="m-0 text-[15px] font-semibold">Stop, partials and close</h2>
                <Pill>Report only</Pill>
              </div>
              <p className="m-0 text-sm leading-relaxed text-sub">
                You're covering with report-only rights, so stop or target moves, partials and closes stay with {t.owner.name}. Ask for manage rights in the handover if you need to record what was done at the broker.
              </p>
            </Card>
          </aside>
        )}
        {canManage && (
          <aside aria-label="Manage trade" className="flex min-w-0 flex-[1_1_320px] flex-col gap-3.5">
            {t.access.covering && <p className="m-0 rounded-lg border border-[#2e2650] bg-[#1a1530] px-3 py-2 text-sm text-ink2">Manage rights: record what was done at the broker for {t.owner.name}. VTF never places orders.</p>}
            <Card className="flex flex-col gap-2.5 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Move stop</h2>
              <div className="flex gap-2">
                <Input mono inputMode="decimal" aria-label="New stop" placeholder={t.stopLoss ?? ''} value={newStop} onChange={(e) => setNewStop(e.target.value)} />
                <Button onClick={() => moveStop.mutate()} disabled={!newStop || moveStop.isPending}>
                  Save
                </Button>
              </div>
              <span className="text-xs text-muted">A wider stop alerts the Risk Manager.</span>
            </Card>
            <Card className="flex flex-col gap-2.5 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Partial close</h2>
              <div className="grid grid-cols-2 gap-2">
                <Input mono inputMode="decimal" aria-label="Exit price" placeholder="Price" value={partPrice} onChange={(e) => setPartPrice(e.target.value)} />
                <Input mono inputMode="decimal" aria-label="Size closed" placeholder={t.sizeOpen ? `Size (of ${t.sizeOpen})` : 'Size'} value={partSize} onChange={(e) => setPartSize(e.target.value)} />
              </div>
              <Button onClick={() => partial.mutate()} disabled={!partPrice || !partSize || partial.isPending}>
                Record partial
              </Button>
            </Card>
            <Card tone="down" className="flex flex-col gap-2.5 p-4">
              <h2 className="m-0 text-[15px] font-semibold">Close trade</h2>
              <div className="flex gap-2">
                <Input mono inputMode="decimal" aria-label="Exit price" placeholder="Exit price" value={closePrice} onChange={(e) => setClosePrice(e.target.value)} />
                <Button variant="danger" onClick={() => close.mutate()} disabled={!closePrice || close.isPending}>
                  Close
                </Button>
              </div>
            </Card>
          </aside>
        )}
      </div>
    </>
  );
}

export function TradesList() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [status, setStatus] = useState('OPEN');
  const [scope, setScope] = useState('floor');
  const { data } = useQuery({ queryKey: ['trades', status, scope], queryFn: () => api<TradeView[]>(`/trades?status=${status}&scope=${scope}`) });
  return (
    <>
      <PageHeader
        title="Trades"
        right={
          <>
            <Segmented label="Status" options={[{ value: 'OPEN', label: 'Open' }, { value: 'CLOSED', label: 'Closed' }]} value={status} onChange={setStatus} />
            <Segmented label="Whose" options={[{ value: 'floor', label: 'Whole floor' }, { value: 'mine', label: 'Mine' }]} value={scope} onChange={setScope} />
          </>
        }
      />
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                {['Symbol', 'Trader', 'Account', 'Side', 'Entry', 'Stop', status === 'OPEN' ? 'Next report' : 'Result', 'Opened'].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.map((t) => (
                <tr key={t.id} className="border-t border-[#1a1f27]">
                  <td className="px-4 py-3 font-semibold">
                    <Link to={`/trades/${t.id}`}>{t.symbol}</Link>
                  </td>
                  <td className="px-4 py-3">{t.owner.name}</td>
                  <td className="px-4 py-3 text-sub">{t.account.code}</td>
                  <td className={`px-4 py-3 font-semibold ${dirClass(t.direction)}`}>{dirLabel(t.direction)}</td>
                  <td className="px-4 py-3 font-mono">{t.avgEntry}</td>
                  <td className="px-4 py-3 font-mono text-sub">{t.stopLoss ?? '—'}</td>
                  <td className="px-4 py-3">{status === 'OPEN' ? (t.nextReport ? `#${t.nextReport.sequence} ${fmtTime(t.nextReport.dueAt, tz)} · ${t.nextReport.status.toLowerCase()}` : '—') : <span className={signClass(t.realisedR)}>{fmtR(t.realisedR)}</span>}</td>
                  <td className="px-4 py-3 text-sub">{fmtTime(t.openedAt, tz, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!data?.length && <Empty>No trades.</Empty>}
        </div>
      </Card>
    </>
  );
}
